// Client Billing Overview — mirrors Matter BillingOverviewSection
// Shows aggregate KPIs + AR aging + collection rate across ALL client matters

import React from 'react';
import type { ClientBillingStats } from '../../../hooks/useClientBilling';

interface ClientBillingOverviewProps {
  stats: ClientBillingStats;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const ClientBillingOverview: React.FC<ClientBillingOverviewProps> = ({ stats }) => {
  const arTotal = stats.arCurrent + stats.ar31to60 + stats.ar61to90 + stats.ar91Plus;

  return (
    <div className="space-y-6">
      {/* Summary Cards — same layout as Matter BillingOverviewSection */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-lex-accent-soft p-4 rounded-lg border border-lex-accent">
          <div className="text-lex-accent text-sm font-medium">Time (WIP)</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
            {formatCurrency(stats.unbilledTimeAmount)}
          </div>
          <div className="text-xs text-lex-accent mt-1">
            {stats.totalHours.toFixed(1)} hrs ({stats.timeEntryCount} entries)
          </div>
        </div>

        <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
          <div className="text-amber-600 dark:text-amber-400 text-sm font-medium">Expenses (WIP)</div>
          <div className="text-2xl font-bold text-amber-900 dark:text-amber-100 tabular-nums">
            {formatCurrency(stats.unbilledExpenses)}
          </div>
          <div className="text-xs text-amber-500 dark:text-amber-400 mt-1">
            {stats.expenseCount} expense{stats.expenseCount !== 1 ? 's' : ''}
          </div>
        </div>

        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
          <div className="text-green-600 dark:text-green-400 text-sm font-medium">Total Billed</div>
          <div className="text-2xl font-bold text-green-900 dark:text-green-100 tabular-nums">
            {formatCurrency(stats.totalBilled)}
          </div>
        </div>

        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
          <div className="text-purple-600 dark:text-purple-400 text-sm font-medium">Outstanding</div>
          <div className="text-2xl font-bold text-purple-900 dark:text-purple-100 tabular-nums">
            {formatCurrency(stats.billsOutstanding)}
          </div>
          <div className="text-xs text-purple-500 dark:text-purple-400 mt-1">
            {stats.billCount} invoice{stats.billCount !== 1 ? 's' : ''} across {stats.matterCount} matter{stats.matterCount !== 1 ? 's' : ''}
          </div>
        </div>

        <div className="bg-teal-50 dark:bg-teal-900/20 p-4 rounded-lg border border-teal-200 dark:border-teal-800">
          <div className="text-teal-600 dark:text-teal-400 text-sm font-medium">Trust Balance</div>
          <div className="text-2xl font-bold text-teal-900 dark:text-teal-100 tabular-nums">
            {formatCurrency(stats.trustBalance)}
          </div>
          <div className="text-xs text-teal-500 dark:text-teal-400 mt-1">
            {stats.trustTransactionCount} transaction{stats.trustTransactionCount !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* AR Aging Breakdown — client-specific */}
      {arTotal > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Accounts Receivable Aging</div>
          <div className="flex rounded-lg overflow-hidden h-6 mb-2">
            {stats.arCurrent > 0 && (
              <div
                className="bg-green-400 dark:bg-green-600 flex items-center justify-center text-xs text-white font-medium"
                style={{ width: `${Math.max((stats.arCurrent / arTotal) * 100, 8)}%` }}
              >
                {formatCurrency(stats.arCurrent)}
              </div>
            )}
            {stats.ar31to60 > 0 && (
              <div
                className="bg-yellow-400 dark:bg-yellow-600 flex items-center justify-center text-xs text-white font-medium"
                style={{ width: `${Math.max((stats.ar31to60 / arTotal) * 100, 8)}%` }}
              >
                {formatCurrency(stats.ar31to60)}
              </div>
            )}
            {stats.ar61to90 > 0 && (
              <div
                className="bg-orange-400 dark:bg-orange-600 flex items-center justify-center text-xs text-white font-medium"
                style={{ width: `${Math.max((stats.ar61to90 / arTotal) * 100, 8)}%` }}
              >
                {formatCurrency(stats.ar61to90)}
              </div>
            )}
            {stats.ar91Plus > 0 && (
              <div
                className="bg-red-400 dark:bg-red-600 flex items-center justify-center text-xs text-white font-medium"
                style={{ width: `${Math.max((stats.ar91Plus / arTotal) * 100, 8)}%` }}
              >
                {formatCurrency(stats.ar91Plus)}
              </div>
            )}
          </div>
          <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400 inline-block" /> Current</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400 inline-block" /> 31-60 days</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400 inline-block" /> 61-90 days</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400 inline-block" /> 90+ days</span>
          </div>
        </div>
      )}

      {/* Collection Summary */}
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Collection Summary</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Collection Rate</label>
            <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{stats.collectionRate.toFixed(1)}%</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {formatCurrency(stats.billsPaid)} of {formatCurrency(stats.billsTotal)} billed
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Active Matters</label>
            <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{stats.matterCount}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">With billing activity</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Trust Deposits</label>
            <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{formatCurrency(stats.trustTotalDeposits)}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {formatCurrency(stats.trustTotalDisbursements)} disbursed
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientBillingOverview;
